
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { exportGuardShiftsToExcel } from "@/pages/scheduling/services/exportService";
import { CalendarEvent } from "@/pages/scheduling/types";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

export const useExportImport = (
  teamId: string,
  getDateRange: () => { start: Date, end: Date },
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<CalendarEvent[], Error>>
) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isExporting, setIsExporting] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  const handleBulkUpload = () => {
    setIsBulkUploadOpen(true);
  };

  const closeBulkUpload = () => {
    setIsBulkUploadOpen(false);
  };

  const handleBulkUploadSuccess = () => {
    refetch();
    setIsBulkUploadOpen(false);
  };

  const handleExportData = async () => {
    if (!teamId) return;
    
    try {
      setIsExporting(true);
      
      const { start, end } = getDateRange();
      const result = await exportGuardShiftsToExcel(teamId, { start, end });
      
      toast({
        title: t("exportSuccess") || "Export Successful",
        description: `${result.recordCount} ${t("recordsExported") || "records exported"}`,
      });
    } catch (error) {
      console.error("Export error:", error);
      
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: error instanceof Error 
          ? error.message 
          : (t("errorExportingData") || "Error exporting data"),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isExporting,
    isBulkUploadOpen,
    handleBulkUpload,
    closeBulkUpload,
    handleBulkUploadSuccess,
    handleExportData
  };
};
